import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
// Utils
import { Form, FormElements } from '@zolteam/zol-react-form';
// Components
import { Button, Skeleton } from '@zolteam/zol-admin-ui';
// Services
import { useGetAdminUserQuery } from '../../services/parazol/parazolQueries';
import { putUpdateAdminUserEmail } from '../../services/parazol/parazolService';
// Context
import { useAppContext } from '../../store/AppContext';
// Locales
import fr from '../../locales/fr.json';

/**
 * @typedef {object} AdminUserEmail
 * @property {number} version
 * @property {string} emailAddress
 * @property {string} emailAddressConfirmationRedirectUrl
 */

const FORM_NAME = 'user-email';

const SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'UtilisateurEmail',
  properties: {
    emailAddress: {
      type: 'string',
      format: 'email',
      minLength: 1,
      description: 'Email',
      x_layout: '1/1/1/7',
    },
  },
  required: ['emailAddress'],
  additionalProperties: false,
};

/**
 * UserEmail component.
 * @returns {JSX.Element}
 */
function UserEmail() {
  // Hooks
  const { t } = useTranslation();
  const { id: userId } = useParams();
  const [, dispatch] = useAppContext();

  // Services
  const {
    isLoading: isLoadingUser,
    data: userData,
    refetch: getAdminUserRefetch,
  } = useGetAdminUserQuery(
    userId,
  );
  const updateAdminUserEmailMutation = useMutation({
    mutationFn: (/** @type {AdminUserEmail} */values) => putUpdateAdminUserEmail(
      userId,
      values,
    ),
    onSuccess: () => {
      dispatch({
        type: 'SET_MESSAGE',
        payload: { text: t('parazol.userForm.changeMailSuccess'), type: 'success' },
      });
      getAdminUserRefetch();
    },
  });

  // Handlers
  const onSubmit = (/** @type {{emailAddress:string}} */values) => {
    /** @type {AdminUserEmail} */
    const newUser = {
      ...values,
      version: userData.version,
      emailAddressConfirmationRedirectUrl: `${import.meta.env.VITE_APP_URL}/confirm-password`,
    };

    updateAdminUserEmailMutation.mutate(newUser);
  };

  return (
    <>
      <h1 className="h1-default margin-top-m margin-bottom-m" style={{ fontWeight: 'bold' }}>
        {t('parazol.userForm.emailUserTitle')}
      </h1>
      {isLoadingUser && !userData
        ? <Skeleton shape="rectangle" height="3.625rem" width="100%" />
        : (
          <Form
            name={FORM_NAME}
            className=""
            callbacks={{
              onSubmit,
            }}
            defaultValues={userData}
            schema={SCHEMA}
            translations={{
              formValidation: fr.cortizol.formValidation,
            }}
          >
            {(formConfig, formInfo) => (
              <FormElements
                formConfig={formConfig}
                formTitle={formInfo.formName}
                translations={fr.cortizol}
              />
            )}
          </Form>
        )}

      <Button
        form={FORM_NAME}
        isSubmit
        isLoading={updateAdminUserEmailMutation.isLoading}
      >
        {t('parazol.userForm.submit')}
      </Button>
    </>
  );
}

export default UserEmail;
